import request from "graphql-request"

import { appInsights } from "../logging/ApplicationInsightsService"
import appInsightsLogger from "../logging/appInsightsLogger"
import {
  getAccessToken,
  requestErrorInterceptor,
  requestInterceptor,
  responseErrorInterceptor,
  responseInterceptor,
} from "../authentication/apiClient"

import { accountApi } from "./api/accountApi"
import { externalApi } from "./api/externalApi"
import { paymentsApi } from "./api/paymentsApi"
import { practiceApi } from "./api/practiceApi"
import { providerApi } from "./api/providerApi"
import { rateApi } from "./api/rateApi"
import { referralApi } from "./api/referralApi"
import { searchApi } from "./api/searchApi"
import { tinValidationApi } from "./api/tinValidationApi"
import axios, { AxiosResponse, AxiosError } from "axios"
import { getFileStatusDisplayId } from "../../domain/Files/helpers"
import { FileStatusDisplayIdEnum } from "../../domain/Files/schema"
import { getRouteWithParams } from "../../routes"
import { getBaseEndpoint } from "../endpoints/endpointManager"
import { ApiError } from "./ApiError"
import { supportApi } from "./api/supportApi"

const DEFAULT_FILE_SIZE = 30
export const MAX_FILE_SIZE_BYTES = DEFAULT_FILE_SIZE * 1024 * 1024

export interface PagedResult<T> {
  items: T[]
  totalCount: number
}

const apiServiceAxios = axios.create({ baseURL: getBaseEndpoint() })

apiServiceAxios.interceptors.request.use(
  requestInterceptor,
  requestErrorInterceptor,
)

apiServiceAxios.interceptors.response.use(
  responseInterceptor,
  responseErrorInterceptor,
)

export const getData = async <T>(
  url: string,
  usePost: boolean = false,
  body?: unknown,
  isBlob?: boolean,
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = usePost
      ? await apiServiceAxios.post<T>(url, body)
      : await apiServiceAxios.get<T>(url, {
          responseType: isBlob ? "blob" : "json",
        })
    if (
      response.status === 200 ||
      response.status === 201 ||
      response.status === 204
    ) {
      return response.data
    } else {
      throw new ApiError(
        `Failed to get data with status code: ${response.status}`,
        response.status,
        { errorCode: "REQUEST_FAILED" },
      )
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      appInsightsLogger.exception(error as AxiosError)
      if (error.response.status === 404) {
        throw new ApiError("Resource not found", error.response.status, {
          data: error.response.data,
          errorCode: "RESOURCE_NOT_FOUND",
        })
      } else if (error.response.status === 429) {
        throw new ApiError("Too many requests", error.response.status, {
          data: error.response.data,
          errorCode: "TOO_MANY_REQUESTS",
        })
      } else {
        throw new ApiError(
          `Failed to get data with status code: ${error.response.status}`,
          error.response.status,
          {
            data: error.response.data,
            errorCode: "API_ERROR",
          },
        )
      }
    } else {
      appInsightsLogger.exception(error as Error)
      throw new ApiError("An unknown error occurred", 500, {
        errorCode: "UNKNOWN_ERROR",
      })
    }
  }
}

export type FileResponse<T> = {
  status: FileStatusDisplayIdEnum
  data?: T
}

export const getFile = async <T>(
  url: string,
  usePost: boolean = false,
  body?: unknown,
  isBlob?: boolean,
): Promise<FileResponse<T>> => {
  try {
    const response: AxiosResponse<T> = usePost
      ? await apiServiceAxios.post<T>(url, body)
      : await apiServiceAxios.get<T>(url, {
          responseType: isBlob ? "blob" : "json",
        })
    if (response.status === 200) {
      return {
        status: getFileStatusDisplayId(response.status),
        data: response.data,
      }
    } else if (response.status === 202) {
      return { status: getFileStatusDisplayId(response.status) }
    } else {
      throw new Error(`Failed to get data with status code: ${response.status}`)
    }
  } catch (error) {
    if (
      axios.isAxiosError(error) &&
      error.response &&
      error.response.status === 410
    ) {
      return { status: getFileStatusDisplayId(error.response.status) }
    } else if (
      axios.isAxiosError(error) &&
      error.response &&
      error.response.status === 404
    ) {
      appInsightsLogger.exception(error as AxiosError)
      throw new Error("Resource not found")
    } else {
      appInsightsLogger.exception(error as Error)
      throw new Error(`Failed to get data with: ${error}`)
    }
  }
}

/**
 * Generic helper to fetch content and return common file status semantics.
 * Mirrors earlier ad-hoc implementations that needed special handling for
 * 200 -> AVAILABLE (blob), 202 -> PROCESSING, 410 -> QUARANTINED.
 *
 * @param url - endpoint path (relative)
 * @returns FileResponse<Blob> with status and optional data blob
 */
export const getContentAndStatus = async (
  url: string,
): Promise<FileResponse<Blob>> => {
  try {
    const response: AxiosResponse<Blob> = await apiServiceAxios.get<Blob>(url, {
      responseType: "blob",
    })
    if (response.status === 200) {
      return {
        status: getFileStatusDisplayId(response.status),
        data: response.data,
      }
    } else if (response.status === 202) {
      return { status: getFileStatusDisplayId(response.status) }
    } else {
      throw new Error(`Failed to get data with status code: ${response.status}`)
    }
  } catch (error) {
    if (
      axios.isAxiosError(error) &&
      error.response &&
      error.response.status === 410
    ) {
      return { status: getFileStatusDisplayId(error.response.status) }
    } else if (
      axios.isAxiosError(error) &&
      error.response &&
      error.response.status === 404
    ) {
      appInsightsLogger.exception(error as AxiosError)
      throw new Error("Resource not found")
    } else {
      appInsightsLogger.exception(error as Error)
      throw new Error(`Failed to get data with: ${error}`)
    }
  }
}

export const postData = async <T>(url: string, data?: unknown): Promise<T> => {
  return getData<T>(url, true, data)
}

export const postDataFlexibleTyped = async <T, R = unknown>(
  url: string,
  data?: R,
): Promise<T> => {
  return getData<T>(url, true, data)
}

export const postFormData = async <T>(
  url: string,
  formData: FormData,
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiServiceAxios.post<T>(
      url,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    )
    if ([200, 201, 204].includes(response.status)) {
      return response.data
    } else {
      throw new ApiError(
        `Failed to post form data with status code: ${response.status}`,
        response.status,
        { errorCode: "REQUEST_FAILED" },
      )
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      appInsightsLogger.exception(error as AxiosError)
      throw new ApiError(
        `Failed to post form data with status code: ${error.response.status}`,
        error.response.status,
        {
          data: error.response.data,
          errorCode: "API_ERROR",
        },
      )
    } else {
      appInsightsLogger.exception(error as Error)
      throw new ApiError("An unknown error occurred", 500, {
        errorCode: "UNKNOWN_ERROR",
      })
    }
  }
}

export const putData = async <T>(url: string, data?: unknown): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiServiceAxios.put<T>(url, data)
    if (response.status === 200 || response.status === 204) {
      return response.data
    } else {
      throw new ApiError(
        `Failed to put data with status code: ${response.status}`,
        response.status,
        { errorCode: "REQUEST_FAILED" },
      )
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      appInsightsLogger.exception(error as AxiosError)
      throw new ApiError(
        `Failed to put data with status code: ${error.response.status}`,
        error.response.status,
        {
          data: error.response.data,
          errorCode: "API_ERROR",
        },
      )
    } else {
      appInsightsLogger.exception(error as Error)
      throw new ApiError("An unknown error occurred", 500, {
        errorCode: "UNKNOWN_ERROR",
      })
    }
  }
}

export const putDataTyped = async <T, R = unknown>(
  url: string,
  data?: R,
): Promise<T> => {
  return putData<T>(url, data)
}

export const deleteData = async <T>(url: string): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiServiceAxios.delete<T>(url)
    if (response.status === 200 || response.status === 204) {
      return response.data
    } else {
      throw new ApiError(
        `Failed to delete data with status code: ${response.status}`,
        response.status,
        { errorCode: "REQUEST_FAILED" },
      )
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      appInsightsLogger.exception(error as AxiosError)
      throw new ApiError(
        `Failed to delete data with status code: ${error.response.status}`,
        error.response.status,
        {
          data: error.response.data,
          errorCode: "API_ERROR",
        },
      )
    } else {
      appInsightsLogger.exception(error as Error)
      throw new ApiError("An unknown error occurred", 500, {
        errorCode: "UNKNOWN_ERROR",
      })
    }
  }
}

export const buildUrl = (urlPath: string, params?: Record<string, string>) => {
  return getRouteWithParams(urlPath, params)
}

export const buildURLWithQueryParams = (
  urlPath: string,
  params?: Record<string, string>,
  queryParams?: Record<string, string>,
): string => {
  const pathWithParams = getRouteWithParams(urlPath, params)
  if (!queryParams) {
    return pathWithParams
  }
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(queryParams)) {
    searchParams.append(key, value)
  }
  return `${pathWithParams}?${searchParams.toString()}`
}

export interface GraphQLRequestOptions {
  url: string
  query: string
}

export async function executeGraphQLQuery<T>({
  url,
  query,
}: GraphQLRequestOptions): Promise<T> {
  try {
    const token = getAccessToken()
    const headers = {
      Authorization: `Bearer ${token}`,
      "veap-user-sessionid":
        appInsights.context.sessionManager.automaticSession.id,
    }
    const response = await request<T>(`${url}/graphql`, query, headers)
    return response
  } catch (error) {
    appInsightsLogger.exception(error as Error)
    throw new Error(`Failed to execute GraphQL query: ${error}`)
  }
}

export const apiService = {
  account: accountApi,
  external: externalApi,
  payments: paymentsApi,
  practice: practiceApi,
  provider: providerApi,
  rate: rateApi,
  referral: referralApi,
  search: searchApi,
  tinValidation: tinValidationApi,
  support: supportApi,
}
